import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt } from '@fortawesome/pro-solid-svg-icons/faBolt';
import { faCopy } from '@fortawesome/pro-light-svg-icons/faCopy';
import { faExternalLinkAlt } from '@fortawesome/pro-solid-svg-icons/faExternalLinkAlt';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';

import messages from './messages';
import styles from './styles.module.less';
import HelpArticleLink from '../../../../components/help-article-link';
import { useBinaryState } from '../../../../hooks/use-binary-state';
import { copyToClipboard } from '../../../../services/copy-to-clipboard';
import { linkIDs } from '../../../../services/link-service/links';
import { createReturnRabbitKey, getReturnRabbitKey } from '../../api/integrations-api';
import { SettingsPageTitle } from '../../components/settings-page-title/settings-page-title';
import ReturnRabbitLogo from '../svgs/returnrabbit-logo.svg';

const shopifyAppUrl = 'https://apps.shopify.com/return_rabbit?st_source=autocomplete';

export const ReturnRabbit = () => {
  const { formatMessage } = useIntl();
  const [returnRabbitKey, setReturnRabbitKey] = useState();
  const [showError, setShowError, setHideError] = useBinaryState();
  const [isLoading, setStartLoading, setStopLoading] = useBinaryState();

  useEffect(() => {
    setStartLoading();
    getReturnRabbitKey()
      .then((response) => {
        setReturnRabbitKey(response.data);
      })
      .finally(setStopLoading);
  }, [setStartLoading, setStopLoading]);

  const generateKeyOnClick = useCallback(() => {
    setStartLoading();
    createReturnRabbitKey()
      .then((response) => {
        setReturnRabbitKey(response.data);
        setHideError();
      })
      .catch(setShowError)
      .finally(setStopLoading);
  }, [setHideError, setShowError, setStartLoading, setStopLoading]);

  const copyReturnRabbitKey = useCallback(() => {
    copyToClipboard(returnRabbitKey);
  }, [returnRabbitKey]);

  const getResponseContent = () => {
    if (showError) {
      return (
        <BannerMessageV2
          className={styles['banner-error']}
          content={<FormattedMessage {...messages.returnRabbitKeyError} />}
          layout={BannerLayout.Inline}
          status={BannerStatus.Error}
        />
      );
    }
    if (returnRabbitKey) {
      return (
        <div className={styles['key-content']}>
          <p className={styles.response}>{returnRabbitKey}</p>
          <div onClick={copyReturnRabbitKey}>
            <FontAwesomeIcon className={styles['copy-icon']} icon={faCopy} />
          </div>
        </div>
      );
    }
    return null;
  };

  const getSubheading = () => {
    let message = messages.alreadyInstalledOnShopify;

    if (returnRabbitKey) {
      message = messages.apiKey;
    }
    return (
      <h4 className={styles['key-generation-subheading']}>
        <FormattedMessage {...message} />
      </h4>
    );
  };

  return (
    <div className={styles['page-wrapper']}>
      <SettingsPageTitle value={messages.returnRabbitHeader} />
      <section className={styles['info-panel']}>
        <div className={styles['info-panel-description']}>
          <FormattedMessage {...messages.returnRabbit} />
          <span className={styles['external-link']}>
            <FormattedMessage
              {...messages.learnMore}
              values={{
                a: (chunks) => (
                  <HelpArticleLink articleId={linkIDs.ReturnRabbit}>
                    {chunks}
                    <FontAwesomeIcon className={styles['external-link-icon']} icon={faExternalLinkAlt} />
                  </HelpArticleLink>
                ),
              }}
            />
          </span>
        </div>
        <img alt={formatMessage(messages.returnRabbitLogo)} className={styles.logo} src={ReturnRabbitLogo} />
      </section>
      <section className={styles['key-generation-section']}>
        <h4 className={styles['key-generation-subheading']}>
          <FormattedMessage {...messages.getStarted} />
        </h4>
        <Button className={styles['install-button']} href={shopifyAppUrl} theme='primary'>
          <FontAwesomeIcon className={styles['bolt-icon']} icon={faBolt} />
          <FormattedMessage {...messages.installOnShopify} />
        </Button>
        {getSubheading()}
        {getResponseContent()}
        <Button className={styles['generate-key-button']} disabled={isLoading} onClick={generateKeyOnClick} theme='tertiary'>
          <FormattedMessage {...(returnRabbitKey ? messages.regenerateKey : messages.generateKey)} />
        </Button>
      </section>
    </div>
  );
};
